import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import {
  useCreatePayeeVendorMutation,
  usePatchPayeeVendorMutation,
} from "#graphql/supergraphHooks";
import {
  type CreatePayeeVendorMutation,
  type PatchPayeeVendorMutation,
} from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { PayeeWorkflowModes } from "../../../../Context/constants";
import {
  useBasePayeeReducerContext,
  usePayeeWorkflowContext,
} from "../../../../Context/PayeeContext";
import { TaxDocumentType } from "../../../types";
import { generatePayeePatchDiff } from "./utils";
import type { PayeeBaseSupportingDocument } from "../../../../types";
import type { PayeeVendorTaxDocument } from "../../../types";

type CreatedVendorResult =
  | CreatePayeeVendorMutation["createPayeeVendor"]
  | PatchPayeeVendorMutation["patchPayeeVendor"]
  | null;

export interface UseSaveVendorPayeeHandlerOptions {
  onSuccess?: (createdPayee?: CreatedVendorResult) => void;
  onError?: (error: Error) => void;
}

export interface UseSaveVendorPayeeHandlerReturn {
  savePayee: () => Promise<void>;
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * Hook that abstracts vendor payee save logic, returning either create or patch mutations
 * based on workflow mode props. Handles success/error handling, and redirect behavior.
 */
export const useSaveVendorPayeeHandler = (
  options: UseSaveVendorPayeeHandlerOptions = {},
): UseSaveVendorPayeeHandlerReturn => {
  const { onSuccess, onError } = options;

  // Access payee data from context
  const payeeData = useBasePayeeReducerContext();
  const {
    arenaId,
    payeeName,
    paymentPref,
    payeeAddress,
    taxCountryId,
    taxId,
    form1099Required,
    invoices,
    taxDocuments,
    payeeId,
  } = payeeData;
  const { mode, originalPayeeData } = usePayeeWorkflowContext();

  const defaultOnSuccess = useCallback(() => {
    window.location.href = "/canopy/treasury/payees";
  }, []);

  const defaultOnError = useCallback((error: Error) => {
    const errorMessage = error instanceof Error ? error.message : "An error occurred";
    toast.error(errorMessage);
  }, []);

  const [createPayeeVendor, { loading: createLoading, error: createError }] =
    useCreatePayeeVendorMutation({
      onCompleted: (data: CreatePayeeVendorMutation) => {
        const createdPayee = data?.createPayeeVendor ?? null;
        onSuccess ? onSuccess(createdPayee) : defaultOnSuccess();
      },
      onError: (error: ApolloError) => {
        addError(formatError(error));
        onError ? onError(error) : defaultOnError(error);
      },
      context: clientContext(),
    });

  const [patchPayeeVendor, { loading: patchLoading, error: patchError }] =
    usePatchPayeeVendorMutation({
      onCompleted: (data: PatchPayeeVendorMutation) => {
        const updatedPayee = data?.patchPayeeVendor ?? null;
        onSuccess ? onSuccess(updatedPayee) : defaultOnSuccess();
      },
      onError: (error: ApolloError) => {
        addError(formatError(error));
        onError ? onError(error) : defaultOnError(error);
      },
      context: clientContext(),
    });

  const savePayee = useCallback(async () => {
    const paymentPrefId = paymentPref?.id;
    if (!arenaId) {
      const error = new Error("Missing required information");
      onError ? onError(error) : defaultOnError(error);
      return;
    }

    try {
      if (mode === PayeeWorkflowModes.EDIT) {
        if (!payeeId) {
          const errorMessage = "Cannot find payee to update";
          toast.error(errorMessage);
          return;
        }

        if (!originalPayeeData) {
          const errorMessage = "Cannot find original payee data for comparison";
          toast.error(errorMessage);
          return;
        }

        // Generate patch diff to only send changed fields
        const patchDiff = generatePayeePatchDiff(payeeData, originalPayeeData);

        if (!patchDiff) {
          // No changes detected, show success without making API call
          onSuccess ? onSuccess() : defaultOnSuccess();
          return;
        }

        await patchPayeeVendor({
          variables: {
            patchPayeeVendorInput: {
              payeeId: payeeId,
              ...patchDiff,
            },
          },
        });
      } else {
        if (!invoices || !paymentPrefId || !payeeName) {
          const error = new Error("Missing required information");
          onError ? onError(error) : defaultOnError(error);
          return;
        }
        await createPayeeVendor({
          variables: {
            createPayeeVendorInput: {
              arenaId: arenaId,
              name: payeeName,
              paymentPrefId: paymentPrefId,
              invoices: invoices.map((doc: PayeeBaseSupportingDocument) => ({
                name: doc.name,
                size: doc.size,
                s3ObjectKey: doc.s3ObjectKey,
              })),
              address: payeeAddress
                ? {
                    street: payeeAddress.street,
                    street2: payeeAddress.street2,
                    city: payeeAddress.city,
                    postalCode: payeeAddress.postalCode,
                    stateId: payeeAddress.stateId,
                    countryId: payeeAddress.countryId,
                    region: payeeAddress.region,
                  }
                : undefined,
              form1099Required:
                form1099Required === "yes"
                  ? true
                  : form1099Required === "no"
                    ? false
                    : undefined,
              taxDocuments:
                taxDocuments && taxDocuments.length > 0
                  ? taxDocuments.map((doc: PayeeVendorTaxDocument) => ({
                      name: doc.name,
                      size: doc.size,
                      s3ObjectKey: doc.s3ObjectKey,
                      documentType:
                        doc.documentType === TaxDocumentType.US_TAX ? "US_TAX" : "NON_US_TAX",
                    }))
                  : undefined,
              taxIdCountry: taxCountryId,
              taxIdUnencrypted: taxId,
            },
          },
        });
      }
    } catch (error) {
      // Apollo errors are reported by the mutation's onError callback above;
      // only report unexpected non-Apollo errors here to avoid double-fire.
      if (!(error instanceof ApolloError)) {
        addError(formatError(error));
      }
      toast.error("Unexpected error while submitting.");
      return;
    }
  }, [
    arenaId,
    payeeName,
    paymentPref,
    payeeAddress,
    taxCountryId,
    taxId,
    form1099Required,
    invoices,
    taxDocuments,
    payeeId,
    mode,
    createPayeeVendor,
    patchPayeeVendor,
    onSuccess,
    onError,
    defaultOnSuccess,
    defaultOnError,
  ]);

  return {
    savePayee,
    loading: createLoading || patchLoading,
    error: createError || patchError,
  };
};
